import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, PenLine, Plus, Send, X } from "lucide-react";
import { errText, fetchSignHub, newClient, sendContractToSign } from "@/lib/bf";
import { waLink } from "@/lib/wa";
import { WhatsAppGlyph } from "@/components/cards/CardShell";

const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit" }) : "");

const NewClient = ({ onDone }) => {
  const [v, setV] = useState({ salon: "", phone: "", email: "" });
  const [busy, setBusy] = useState(false);
  const save = (e) => {
    e.preventDefault();
    setBusy(true);
    newClient(v).then((d) => { toast.success(d.existing ? "Clientul există deja la tine" : "Client adăugat"); onDone(); })
      .catch((er) => toast.error(errText(er, "Completează numele, un telefon și un e-mail valide"))).finally(() => setBusy(false));
  };
  return (
    <form className="bf-sh-new" onSubmit={save} data-testid="bf-sh-new-form">
      {[["salon", "Nume salon / firmă", "text"], ["phone", "Telefon", "tel"], ["email", "E-mail", "email"]].map(([k, l, t]) => (
        <label key={k} className="st-field"><span className="st-label">{l} *</span>
          <input type={t} required value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} data-testid={`bf-sh-new-${k}`} /></label>
      ))}
      <button type="submit" className="st-btn st-btn--light" disabled={busy} data-testid="bf-sh-new-save">{busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Adaugă</button>
    </form>
  );
};

const Row = ({ r, onSent }) => {
  const [busy, setBusy] = useState(false);
  const [wa, setWa] = useState("");
  const send = () => {
    setBusy(true);
    sendContractToSign(r.id).then((d) => { toast.success(`Contract trimis pe e-mail la ${d.to}`); setWa(waLink(d.phone, d.wa_text)); onSent(r.id); })
      .catch((e) => toast.error(errText(e, "Nu am putut trimite contractul"))).finally(() => setBusy(false));
  };
  return (
    <li className="bf-sh-row" data-testid={`bf-sh-row-${r.id}`}>
      <div><b>{r.salon || "Salon"}</b><small>{[r.admin_name, r.phone, r.email, r.city].filter(Boolean).join(" · ")}</small>
        <em className={r.sent_at ? "is-sent" : ""}>{r.sent_at ? `Trimis la semnat ${day(r.sent_at)}` : r.has_data ? "Date completate, netrimis" : "Fără date de contract"}{r.account ? " · are cont" : ""}</em></div>
      <div className="bf-sh-acts">
        <Link className="st-btn st-btn--ghost" to={`/client/${r.id}#contract`} data-testid={`bf-sh-fill-${r.id}`}><PenLine size={14} /> Completează datele</Link>
        <button type="button" className="st-btn st-btn--light" onClick={send} disabled={busy || !r.email} title={r.email ? "" : "Lipsește e-mailul"} data-testid={`bf-sh-send-${r.id}`}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} {r.sent_at ? "Retrimite" : "Trimite la semnat"}</button>
        {wa && <a className="st-btn st-btn--light" href={wa} target="_blank" rel="noopener noreferrer" data-testid={`bf-sh-wa-${r.id}`}><WhatsAppGlyph /> Trimite și pe WhatsApp</a>}
      </div>
    </li>
  );
};

export default function BFSignHub() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const load = () => fetchSignHub().then((d) => setRows(d.rows)).catch(() => setRows(false));
  useEffect(() => { load(); }, []);
  const sent = (id) => setRows((l) => l.map((r) => (r.id === id ? { ...r, sent_at: new Date().toISOString() } : r)));
  const shown = (rows || []).filter((r) => !q.trim() || norm(`${r.salon} ${r.phone} ${r.email} ${r.city}`).includes(norm(q.trim())));
  return (
    <section className="invite-box bf-sh" data-testid="bf-sign-hub">
      <h3><Send size={18} /> Semnează contract</h3>
      <p className="st-hint">Clienții tăi care nu au semnat încă la toate cele 3 firme. Trimite-le contractul pe e-mail și WhatsApp, împreună cu linkul de creare a contului în platformă.</p>
      <div className="bf-sh-bar">
        <input className="bf-ca-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută salon, telefon, e-mail sau oraș" data-testid="bf-sh-search" />
        <button type="button" className="st-btn st-btn--silver" onClick={() => setAdding(!adding)} data-testid="bf-sh-new-toggle">{adding ? <X size={14} /> : <Plus size={14} />} {adding ? "Renunță" : "Client nou"}</button>
      </div>
      {adding && <NewClient onDone={() => { setAdding(false); load(); }} />}
      {rows === null && <p className="st-hint"><Loader2 size={14} className="animate-spin" /> Se încarcă…</p>}
      {rows === false && <p className="st-hint">Nu am putut încărca lista.</p>}
      {rows && <ul className="bf-sh-list" data-testid="bf-sh-list">{shown.map((r) => <Row key={r.id} r={r} onSent={sent} />)}</ul>}
      {rows && !shown.length && <p className="st-hint" data-testid="bf-sh-empty">{rows.length ? "Niciun client găsit." : "Toți clienții tăi au semnat."}</p>}
    </section>
  );
}
